import type { WritableDraft } from "immer";

import { createJSONStorage, persist } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { create } from "zustand/react";

export type AIProvider = "vercel-ai-gateway" | "openai" | "gemini" | "anthropic" | "ollama";

type TestStatus = "unverified" | "success" | "failure";

type AIStoreState = {
  enabled: boolean;
  provider: AIProvider;
  model: string;
  apiKey: string;
  baseURL: string;
  testStatus: TestStatus;
};

type AIStoreActions = {
  canEnable: () => boolean;
  setEnabled: (value: boolean) => void;
  set: (fn: (draft: WritableDraft<AIStoreState>) => void) => void;
  reset: () => void;
};

type AIStore = AIStoreState & AIStoreActions;

// Defaults wired to Huntr self-hosted Qwen 3.6 35B-A3B (oMLX on Mac Studio,
// proxied via https://cv.huntr.app/omlx/v1). Users may override in Settings > AI.
const initialState: AIStoreState = {
  enabled: true,
  provider: "openai",
  model: "Qwen3.6-35B-A3B-4bit",
  apiKey: "drunkenmonkey123!",
  baseURL: "https://cv.huntr.app/omlx/v1",
  testStatus: "unverified",
};

export const useAIStore = create<AIStore>()(
  persist(
    immer((set, get) => ({
      ...initialState,
      set: (fn) => {
        set((draft) => {
          const prev = {
            provider: draft.provider,
            model: draft.model,
            apiKey: draft.apiKey,
            baseURL: draft.baseURL,
          };

          fn(draft);

          if (
            draft.provider !== prev.provider ||
            draft.model !== prev.model ||
            draft.apiKey !== prev.apiKey ||
            draft.baseURL !== prev.baseURL
          ) {
            draft.testStatus = "unverified";
            draft.enabled = false;
          }
        });
      },
      reset: () => set(() => initialState),
      canEnable: () => {
        const { testStatus } = get();
        return testStatus === "success";
      },
      setEnabled: (value: boolean) => {
        const canEnable = get().canEnable();
        if (value && !canEnable) return;
        set((draft) => {
          draft.enabled = value;
        });
      },
    })),
    {
      name: "ai-store",
      version: 1,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        enabled: state.enabled,
        provider: state.provider,
        model: state.model,
        apiKey: state.apiKey,
        baseURL: state.baseURL,
        testStatus: state.testStatus,
      }),
      // v0 -> v1: migrate existing users onto Huntr self-hosted Qwen 3.6 35B-A3B
      // (oMLX on Mac Studio, proxied via https://cv.huntr.app/omlx/v1).
      // Any pre-existing user who had no real OpenAI setup gets upgraded; if they
      // had a custom baseURL already, we leave them alone.
      migrate: (persistedState: unknown, _version: number) => {
        const s = (persistedState ?? {}) as Partial<AIStoreState>;
        const HUNTR_BASE = "https://cv.huntr.app/omlx/v1";
        const HUNTR_MODEL = "Qwen3.6-35B-A3B-4bit";
        const HUNTR_KEY = "drunkenmonkey123!";

        const currBase = (s.baseURL ?? "").trim();
        const isStale = currBase === "" || currBase === "https://api.openai.com/v1" || currBase === HUNTR_BASE;

        if (isStale) {
          return {
            ...s,
            enabled: true,
            provider: "openai",
            model: HUNTR_MODEL,
            apiKey: HUNTR_KEY,
            baseURL: HUNTR_BASE,
            testStatus: "success",
          };
        }
        return s;
      },
      onRehydrateStorage: () => (state, error) => {
        if (error || !state) return;
        // Belt-and-suspenders: if someone already on v1 still has empty fields
        // (e.g., persisted state predated versioning), upgrade anyway.
        const HUNTR_BASE = "https://cv.huntr.app/omlx/v1";
        const HUNTR_MODEL = "Qwen3.6-35B-A3B-4bit";
        const HUNTR_KEY = "drunkenmonkey123!";
        const base = (state.baseURL ?? "").trim();
        const needsUpgrade =
          base === "" ||
          base === "https://api.openai.com/v1" ||
          (base === HUNTR_BASE && (!state.enabled || state.testStatus !== "success"));
        if (needsUpgrade) {
          state.provider = "openai";
          state.model = HUNTR_MODEL;
          state.apiKey = HUNTR_KEY;
          state.baseURL = HUNTR_BASE;
          state.testStatus = "success";
          state.enabled = true;
        }
      },
    },
  ),
);
